import { useState } from 'react';
import { CoachCard } from '../concept/coach-card';
import type { Note } from '../../lib/interfaces';

const dateOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * What the coach wrote for the client, as the client sees it.
 *
 * A separate component, not the coach's card with the editor hidden: this one
 * never holds a coach-only note to hide. The server answers a share reader
 * with audience 'client' and nothing else, so everything here was meant for
 * them — and it renders nothing at all when there is nothing, since an empty
 * "From your coach" box reads as a coach who has not bothered.
 */
export const CoachMessagesCard: React.FC<{
  notes: Note[];
  /** Show this many, with a way to the rest. */
  limit?: number;
  className?: string;
}> = ({ notes, limit = 3, className }) => {
  const [all, setAll] = useState(false);
  if (notes.length === 0) return null;
  const shown = all ? notes : notes.slice(0, limit);

  return (
    <CoachCard title="From your coach" testId="coach-messages" className={className}>
      <ul className="flex flex-col gap-3">
        {shown.map((n) => (
          <li
            key={n.ID}
            data-testid="coach-message"
            className="flex flex-col gap-1 border-b border-divider pb-3 last:border-b-0 last:pb-0"
          >
            <span className="tabular text-[10.5px] text-muted-foreground">
              {dateOf(n.createdAt)}
            </span>
            <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed">{n.body}</p>
          </li>
        ))}
      </ul>
      {notes.length > limit && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="self-start text-[11px] font-medium text-primary hover:underline"
        >
          {all ? 'Show fewer' : `All ${notes.length} messages`}
        </button>
      )}
    </CoachCard>
  );
};
